import React, { useState } from 'react';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import type { EventMember, EventTable, SfdcUser } from '../../event-manager-client';
import { isClayHost, getAccountBadge } from './utils';
import { MemberCard } from './MemberCard';
import { EmptySeat } from './EmptySeat';
import { OvalDiagram } from './OvalDiagram';

interface TableCardProps {
  table: EventTable;
  members: EventMember[];
  sfdcUsers: SfdcUser[];
  effectiveAssignments: Map<number, { tableId: number | null; seatPosition: number | null }>;
  pendingMemberIds: Set<number>;
  onMoveMember: (memberId: number, tableId: number | null, seatPosition?: number | null) => void;
  onPatchTable: (tableId: number, fields: Partial<{ table_name: string; capacity: number }>) => void;
  onDeleteTable: (tableId: number) => void;
  onSelectMember: (member: EventMember) => void;
}

export function TableCard({ table, members, sfdcUsers, effectiveAssignments, pendingMemberIds, onMoveMember, onPatchTable, onDeleteTable, onSelectMember }: TableCardProps) {
  const [over, setOver] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [nameValue, setNameValue] = useState(table.table_name);
  const [capacityValue, setCapacityValue] = useState(String(table.capacity));

  const emptySlots = Math.max(0, table.capacity - members.length);

  // Compute actual occupied seatPositions for this table to avoid collisions
  const occupiedPositions = new Set(
    members.map(m => effectiveAssignments.get(m.id)?.seatPosition).filter((p): p is number => p != null)
  );

  // Generate non-colliding positions for empty seats
  const emptyPositions: number[] = [];
  let nextPos = 0;
  for (let i = 0; i < emptySlots; i++) {
    while (occupiedPositions.has(nextPos)) nextPos++;
    emptyPositions.push(nextPos);
    nextPos++;
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const memberId = Number(e.dataTransfer.getData('text/plain'));
    if (isNaN(memberId)) return;
    const pos = emptyPositions[0] ?? members.length;
    onMoveMember(memberId, table.id, pos);
  };

  const handleSaveName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== table.table_name) {
      onPatchTable(table.id, { table_name: trimmed });
    }
    setEditingName(false);
  };

  const handleSaveCapacity = () => {
    const num = parseInt(capacityValue, 10);
    if (!isNaN(num) && num >= 1 && num !== table.capacity) {
      if (num < members.length) {
        setCapacityValue(String(table.capacity));
      } else {
        onPatchTable(table.id, { capacity: num });
      }
    }
    setEditingCapacity(false);
  };

  const handleDelete = () => {
    onDeleteTable(table.id);
  };

  const handleSeatDrop = (seatPosition: number) => (memberId: number) => {
    onMoveMember(memberId, table.id, seatPosition);
  };

  const useTwoCol = table.capacity >= 8;

  // Build a unified list of all seats (occupied + empty) sorted by position
  // so the card list matches the clockwise oval order
  const sortedSeats: Array<{ pos: number; member: EventMember | null }> = [];
  const memberByPos = new Map<number, EventMember>();
  for (const m of members) {
    const pos = effectiveAssignments.get(m.id)?.seatPosition;
    if (pos != null) memberByPos.set(pos, m);
  }
  for (let i = 0; i < table.capacity; i++) {
    sortedSeats.push({ pos: i, member: memberByPos.get(i) ?? null });
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragEnter={() => setOver(true)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={handleDrop}
      className={`rounded-xl border bg-white dark:bg-nightshade-900 transition-colors ${
        over
          ? 'border-blueberry-400 dark:border-blueberry-500 shadow-md'
          : 'border-nightshade-200 dark:border-nightshade-700'
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-nightshade-100 dark:border-nightshade-800">
        {editingName ? (
          <div className="flex items-center gap-1 flex-1">
            <input
              autoFocus
              value={nameValue}
              onChange={e => setNameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') { setNameValue(table.table_name); setEditingName(false); } }}
              className="flex-1 text-sm font-semibold bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded px-2 py-0.5 text-nightshade-800 dark:text-nightshade-200 focus:outline-none focus:ring-1 focus:ring-blueberry-400"
            />
            <button onClick={handleSaveName} className="p-0.5 text-matcha-600 hover:text-matcha-700"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => { setNameValue(table.table_name); setEditingName(false); }} className="p-0.5 text-nightshade-400 hover:text-nightshade-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <h3
            className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-800 dark:text-nightshade-200 flex-1 truncate cursor-pointer hover:text-blueberry-500 transition-colors"
            onClick={() => { setNameValue(table.table_name); setEditingName(true); }}
          >
            {table.table_name}
          </h3>
        )}

        {/* Capacity badge */}
        {editingCapacity ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              type="number"
              min={1}
              value={capacityValue}
              onChange={e => setCapacityValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveCapacity(); if (e.key === 'Escape') { setCapacityValue(String(table.capacity)); setEditingCapacity(false); } }}
              className="w-12 text-xs bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded px-1.5 py-0.5 text-center text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-1 focus:ring-blueberry-400"
            />
            <button onClick={handleSaveCapacity} className="p-0.5 text-matcha-600 hover:text-matcha-700"><Check className="w-3 h-3" /></button>
          </div>
        ) : (
          <button
            onClick={() => { setCapacityValue(String(table.capacity)); setEditingCapacity(true); }}
            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-600 dark:text-nightshade-400 hover:bg-nightshade-200 dark:hover:bg-nightshade-700 transition-colors"
          >
            {members.length}/{table.capacity}
          </button>
        )}

        {/* Edit / Delete */}
        <button
          onClick={() => { setNameValue(table.table_name); setEditingName(true); }}
          className="p-1 rounded hover:bg-nightshade-100 dark:hover:bg-nightshade-800 text-nightshade-400 hover:text-nightshade-600 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleDelete}
          className="p-1 rounded transition-colors hover:bg-nightshade-100 dark:hover:bg-nightshade-800 text-nightshade-400 hover:text-pomegranate-500"
          title="Delete table"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Oval diagram */}
      <OvalDiagram
        capacity={table.capacity}
        members={members}
        sfdcUsers={sfdcUsers}
        effectiveAssignments={effectiveAssignments}
        tableId={table.id}
        tableName={table.table_name}
      />

      {/* Card list — sorted by seat position to match clockwise oval order */}
      <div className={`p-3 grid gap-2 ${useTwoCol ? 'grid-cols-2' : ''}`}>
        {sortedSeats.map(({ member: m, pos }) => (
          m ? (
            <MemberCard
              key={m.id}
              member={m}
              onClick={() => onSelectMember(m)}
              isClayHost={isClayHost(m, sfdcUsers)}
              accountBadge={getAccountBadge(m.account_type)}
              isPending={pendingMemberIds.has(m.id)}
              onSwapDrop={(draggedId) => {
                // Only allow swap if dragged member is already seated (not from unassigned)
                const draggedAssignment = effectiveAssignments.get(draggedId);
                if (draggedAssignment?.tableId == null) return;
                onMoveMember(draggedId, table.id, pos);
              }}
            />
          ) : (
            <EmptySeat key={`empty-${pos}`} onDrop={handleSeatDrop(pos)} />
          )
        ))}
      </div>
    </div>
  );
}
